import { useLocation, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { Panel } from '@/components/Panel';
import { FormError } from '@/components/form/FormError';
import { TextField } from '@/components/form/TextField';
import { Button } from '@/components/ui/button';
import { useSubmit } from '@/hooks/useSubmit';
import CurrentUserModel from '@/models/services/CurrentUserModel';
import { useAuthStore } from '@/stores/useAuthStore';

const schema = z.object({
  firstName: z.string().trim().min(1, 'Prénom requis'),
  lastName: z.string().trim().min(1, 'Nom requis'),
});

type ProfileForm = z.infer<typeof schema>;

/**
 * Mon profil : prénom et nom (PATCH /users/me). L'email, clé de rapprochement avec l'IAM, n'est pas modifiable ici.
 * Après enregistrement : retour à la page d'où l'on venait (transmise par la sidebar), sinon à la page d'accueil du rôle.
 */
export function ProfilePage() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from;
  const updateUser = useAuthStore((state) => state.updateUser);
  const { submit, submitting, error } = useSubmit();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileForm>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: user?.firstName ?? '', lastName: user?.lastName ?? '' },
  });

  const onSubmit = (values: ProfileForm) =>
    submit(async () => {
      const { model } = await new CurrentUserModel().save(values, { silentError: true });
      updateUser({ firstName: model.firstName, lastName: model.lastName });
      toast.success('Votre profil a été enregistré.');
      navigate(from ?? '/', { replace: true });
    });

  return (
    <Panel className="max-w-md p-6 space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <TextField id="email" label="Email" value={user?.email ?? ''} disabled readOnly />
        <TextField id="lastName" label="Nom" required error={errors.lastName?.message} {...register('lastName')} />
        <TextField
          id="firstName"
          label="Prénom"
          required
          error={errors.firstName?.message}
          {...register('firstName')}
        />

        <FormError error={error} />

        <Button type="submit" disabled={submitting}>
          <Save className="w-4 h-4" />
          Enregistrer
        </Button>
      </form>
    </Panel>
  );
}
